import { useState } from 'react';
import { Fingerprint, Copy, Check, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';

const UuidGenerator = () => {
  const [uuids, setUuids] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const generateUUIDs = () => {
    const newUuids: string[] = [];
    for (let i = 0; i < quantity; i++) {
      newUuids.push(crypto.randomUUID());
    }
    setUuids(newUuids);
    toast.success(`Generated ${quantity} UUID${quantity > 1 ? 's' : ''}!`);
  };

  const copyUUID = (index: number) => {
    navigator.clipboard.writeText(uuids[index]);
    setCopiedIndex(index);
    toast.success('UUID copied!');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const copyAll = () => {
    navigator.clipboard.writeText(uuids.join('\n'));
    setCopiedAll(true);
    toast.success('All UUIDs copied!');
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const clearAll = () => {
    setUuids([]);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-primary/20">
          <Fingerprint className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">UUID Generator</h1>
          <p className="text-muted-foreground">
            Generate random UUIDs (Version 4)
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle>Generate UUIDs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quantity Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Quantity</Label>
                <span className="text-sm font-mono bg-muted px-2 py-1 rounded">
                  {quantity}
                </span>
              </div>
              <Slider
                value={[quantity]}
                onValueChange={(value) => setQuantity(value[0])}
                min={1}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span>
                <span>50</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button onClick={generateUUIDs} className="gap-2 flex-1">
                <RefreshCw className="h-4 w-4" />
                Generate
              </Button>
              {uuids.length > 0 && (
                <>
                  <Button variant="outline" onClick={copyAll} className="gap-2">
                    {copiedAll ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                    Copy All
                  </Button>
                  <Button variant="ghost" onClick={clearAll}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>

            {/* UUID List */}
            {uuids.length > 0 && (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {uuids.map((uuid, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 group"
                  >
                    <span className="text-xs text-muted-foreground w-6">
                      {index + 1}.
                    </span>
                    <Input
                      value={uuid}
                      readOnly
                      className="font-mono text-sm bg-transparent border-0 focus-visible:ring-0"
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => copyUUID(index)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {copiedIndex === index ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {uuids.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Fingerprint className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Click "Generate" to create UUIDs</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UuidGenerator;